<template>
    <div class="settings-wrapper">
        <div class="settings-content">
            <div class="settings-grid" v-if="settingsTabInView === 'general'">
                <label id="colour-palette-label">Colour theme</label>
                <div class="palette-sections">
                    <section
                        v-for="paletteGroup in paletteGroups"
                        :key="paletteGroup.id"
                        class="palette-section"
                    >
                        <h3 :id="`colour-palette-${paletteGroup.id}-label`" class="palette-section-title">
                            {{ paletteGroup.name }}
                        </h3>
                        <select-button
                            :model-value="selectedColourPaletteId"
                            :options="paletteGroup.options"
                            option-label="name"
                            option-value="id"
                            :allow-empty="false"
                            class="palette-select-button"
                            :aria-labelledby="`colour-palette-label colour-palette-${paletteGroup.id}-label`"
                            @update:model-value="updateTheme"
                        >
                            <template #option="{ option }">
                                <span
                                    class="palette-option-content"
                                    :data-selected="option.id === selectedColourPaletteId"
                                    :aria-label="option.description ? `${option.name}. ${option.description}` : option.name"
                                    :data-testid="`colour-palette-${option.id}`"
                                    v-tooltip.top="option.description ? { value: option.description, showDelay: 250 } : null"
                                >
                                    <span class="palette-option-main">
                                        <span class="palette-name">{{ option.name }}</span>
                                        <span
                                            v-if="option.isRecommended"
                                            class="palette-badge recommended"
                                            data-testid="recommended-palette-badge"
                                        >
                                            Recommended
                                        </span>
                                    </span>
                                    <span class="palette-swatches" aria-hidden="true">
                                        <span
                                            v-for="swatch in option.swatches"
                                            :key="swatch"
                                            class="palette-swatch"
                                            :style="{ backgroundColor: swatch }"
                                        />
                                    </span>
                                </span>
                            </template>
                        </select-button>
                    </section>
                </div>

                <label for="sparkInterestAreas">Your feed interest areas</label>
                <div class="row interest-area-row">
                    <site-area-relevance-selector
                        v-model="selectedInterestAreas"
                        :options="siteAreaRelevanceOptions"
                        require-at-least-one
                    />
                </div>

            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useUIStore } from '@/stores/uiStore.ts';
import SelectButton from 'primevue/selectbutton';
import { useDeltaUserSettingsStore } from '@/stores/deltaGpt/deltaGptUserSettingsStore.ts';
import { useDeltaGptStore } from '@/stores/deltaGpt/deltaGptStore.ts';
import SiteAreaRelevanceSelector from '@/components/community/SiteAreaRelevanceSelector.vue';
import { SITE_AREA_RELEVANCE_OPTIONS } from '@/utils/siteAreaRelevanceUtils.ts';
import {
    AUTOMATIC_COLOUR_PALETTE_OPTIONS,
    STATIC_COLOUR_PALETTE_OPTIONS,
    resolveColourPaletteId,
} from '@/constants/ColourPalettes.ts';
import type { ColourPaletteId, IColourPaletteOption } from '@/constants/ColourPalettes.ts';

const uiStore = useUIStore();
const deltaUserSettingsStore = useDeltaUserSettingsStore();
const deltaGptStore = useDeltaGptStore();

const settingsTabInView = ref<string>('general');
const paletteGroups: ReadonlyArray<{
    id: 'static' | 'automatic';
    name: string;
    options: Array<IColourPaletteOption>;
}> = [
    {
        id: 'static',
        name: 'Static',
        options: [...STATIC_COLOUR_PALETTE_OPTIONS],
    },
    {
        id: 'automatic',
        name: 'Automatic',
        options: [...AUTOMATIC_COLOUR_PALETTE_OPTIONS],
    },
];

const siteAreaRelevanceOptions = SITE_AREA_RELEVANCE_OPTIONS;
const selectedInterestAreas = computed<Array<string>>({
    get: () => deltaUserSettingsStore.userSettings.interestAreas,
    set: (interestAreas) => {
        deltaUserSettingsStore.updateUserSettings({ interestAreas });
        deltaGptStore.fetchAllDocumentInfo();
    },
});
const selectedColourPaletteId = computed<ColourPaletteId>(() => uiStore.colourPaletteId);

function updateTheme (newColourPaletteId: unknown): void {
    const resolvedColourPaletteId = resolveColourPaletteId(
        newColourPaletteId,
        deltaUserSettingsStore.userSettings?.isDarkMode ?? uiStore.isDarkTheme,
    );

    uiStore.applyColourPalette(resolvedColourPaletteId);
    deltaUserSettingsStore.updateUserSettings({
        colourPaletteId: resolvedColourPaletteId,
        isDarkMode: uiStore.isDarkTheme,
    });
}

</script>

<style lang="scss" scoped>
@use '@/assets/scss/components/siteSettings/colourPaletteSelector';

.settings-wrapper {
    display: grid;
    grid-template-rows: auto 1fr;
    height: 100%;
    overflow: hidden;
    min-width: 0;

    .settings-content {
        min-width: 0;
        overflow-x: hidden;
        overflow-y: auto;
        padding: var(--defaultSpacing);
    }
}

.settings-grid {
    align-items: start;
    display: grid;
    gap: var(--largeSpacing);
    grid-template-columns: auto 1fr;
    min-width: 0;
}

.row {
    display: flex;
    gap: 10px;
    align-items: center;
    font-size: var(--smallFontSize);
    min-height: 40px;
}

.interest-area-row {
    align-items: flex-start;
    height: auto;
    width: 100%;
}

:deep(.site-area-relevance-selector) {
    width: 100%;
}

.label {
    margin-right: 10px;
}
</style>
